import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePatientFlow } from '../../context/PatientFlowContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle, Download, Calendar, FileText, Home, Mail, X, ArrowRight } from 'lucide-react';
import { Toaster } from '../../components/Toaster';
import { toast } from '../../hooks/use-toast';

// Mock Email Confirmation Modal (Item 3)
const EmailConfirmationModal = ({ payment, offer, onClose }) => {
  const { t } = useTranslation();
  const [showEmailContent, setShowEmailContent] = useState(false);
  
  useEffect(() => {
    // Simulate email being sent
    const timer = setTimeout(() => {
      setShowEmailContent(true);
      toast({
        title: '📧 Email Sent',
        description: 'A confirmation email has been sent to your registered email address.',
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            <span className="font-semibold">{t('patient.journey.confirmationEmail')}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {!showEmailContent ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">{t('patient.journey.sendingConfirmation')}</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Email Header */}
            <div className="border-b pb-4 mb-4">
              <p className="text-sm text-gray-500">From: <span className="text-gray-900">TreatX Health &lt;noreply@treatx.health&gt;</span></p>
              <p className="text-sm text-gray-500">Subject: <span className="text-gray-900 font-semibold">✅ Payment Confirmed - Order #{payment.id}</span></p>
            </div>
            
            {/* Email Body */}
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-2" />
                <h3 className="font-bold text-lg text-green-800">Payment Successful!</h3>
                <p className="text-sm text-green-700">{t('patient.journey.depositReceived')}</p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Order Details:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Reference:</span>
                    <span className="font-mono font-semibold">{payment.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Provider:</span>
                    <span className="font-semibold">{offer.clinicName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Treatment:</span>
                    <span className="font-semibold">{offer.treatment}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Payment Breakdown:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Deposit Paid:</span>
                    <span className="font-bold text-green-600">€{offer.depositAmount?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remaining Balance:</span>
                    <span className="font-semibold">€{((offer.totalPrice || 0) - (offer.depositAmount || 0)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-gray-600">Total Package Price:</span>
                    <span className="font-bold">€{offer.totalPrice?.toLocaleString() || '0'}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Next Steps:</h4>
                <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                  <li>Your clinic representative will contact you within 24 hours</li>
                  <li>{t('patient.journey.preTreatmentInstructions')}</li>
                  <li>Balance payment due 7 days before your arrival date</li>
                  <li>{t('patient.journey.downloadApp')}</li>
                </ol>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={onClose}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Track Your Journey
                </Button>
              </div>
            </div>
            
            {/* Email Footer */}
            <div className="border-t mt-6 pt-4 text-center text-xs text-gray-500">
              <p>© 2024 TreatX Health. All rights reserved.</p>
              <p className="mt-1">Questions? Contact us at support@treatx.health</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateFlowState, resetFlow } = usePatientFlow();
  const [payment, setPayment] = useState(null);
  const [offer, setOffer] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    // Retrieve payment details from sessionStorage
    const lastPayment = sessionStorage.getItem('lastPayment');
    const paidOffer = sessionStorage.getItem('paidOffer');
    
    if (lastPayment && paidOffer) {
      setPayment(JSON.parse(lastPayment));
      setOffer(JSON.parse(paidOffer));
      
      // Update flow state to mark payment as complete
      updateFlowState({
        paymentCompleted: true,
        currentStep: 'payment-complete'
      });
      
      // Show email confirmation modal after a short delay (Item 3)
      setTimeout(() => {
        setShowEmailModal(true);
      }, 1000);
    } else {
      // No payment data, redirect to dashboard
      navigate('/patient/dashboard');
    }
  }, [navigate, updateFlowState]);

  const handleDownloadReceipt = () => {
    // Generate mock PDF receipt (Priority 1 - Item 2)
    const receiptContent = `
TREATX HEALTH - PAYMENT RECEIPT
================================

RECEIPT #: ${payment.id}
DATE: ${formatDateLocale(payment.createdAt)}
TIME: ${new Date(payment.createdAt).toLocaleTimeString()}

PATIENT INFORMATION
-------------------
Name: ${offer.patientName || 'Patient'}
Email: ${offer.patientEmail || 'N/A'}

PROVIDER INFORMATION
--------------------
Clinic: ${offer.clinicName}
Treatment: ${offer.treatment}

PAYMENT DETAILS
---------------
Deposit Amount:     €${(offer.depositAmount || 0).toLocaleString()}
Payment Method:     Card ending in ${payment.cardLast4 || '****'}
Transaction ID:     ${payment.id}

PACKAGE SUMMARY
---------------
Total Package Price: €${(offer.totalPrice || 0).toLocaleString()}
Deposit Paid:        €${(offer.depositAmount || 0).toLocaleString()}
Remaining Balance:   €${((offer.totalPrice || 0) - (offer.depositAmount || 0)).toLocaleString()}

Balance Due: 7 days before treatment date

================================
Thank you for choosing TreatX Health!
For questions: support@treatx.health

This receipt was generated automatically.
© ${new Date().getFullYear()} TreatX Health. All rights reserved.
    `;
    
    // Create and download mock PDF (as text file for demo)
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TreatX_Receipt_${payment.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: '✅ Receipt Downloaded',
      description: 'Your payment receipt has been downloaded successfully.',
    });
  };

  if (!payment || !offer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Toaster />
      
      {/* Email Confirmation Modal (Item 3) */}
      {showEmailModal && (
        <EmailConfirmationModal 
          payment={payment} 
          offer={offer} 
          onClose={() => setShowEmailModal(false)} 
        />
      )}
      
      <Card className="max-w-2xl w-full">
        <CardContent className="pt-8 pb-6">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('patient.paymentSuccess.title')}</h1>
            <p className="text-gray-600">{t('patient.paymentSuccess.subtitle')}</p>
          </div>

          {/* Payment Details */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">{t('patient.payment.receipt')}</span>
              <span className="font-mono font-semibold">{payment.id}</span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">{t('patient.payment.amount')}</span>
              <span className="text-2xl font-bold text-green-600">
                €{offer.depositAmount.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">{t('patient.payment.method')}</span>
              <span className="font-semibold">Card ending in {payment.cardLast4}</span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">{t('patient.orderDetail.clinic')}</span>
              <span className="font-semibold text-right">{offer.clinicName}</span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">{t('patient.orderDetail.treatment')}</span>
              <span className="font-semibold text-right">{offer.treatment}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('patient.payment.date')}</span>
              <span className="font-semibold">
                {formatDateLocale(payment.createdAt)}
              </span>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              What Happens Next?
            </h3>
            <ol className="space-y-2 text-sm text-blue-900">
              <li className="flex items-start gap-2">
                <span className="font-semibold flex-shrink-0">1.</span>
                <span>The clinic will contact you within 24 hours to confirm your treatment schedule</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold flex-shrink-0">2.</span>
                <span>{t('patient.journey.detailedInstructions')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold flex-shrink-0">3.</span>
                <span>Balance payment of €{(offer.totalPrice - offer.depositAmount).toLocaleString()} is due 7 days before arrival</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold flex-shrink-0">4.</span>
                <span>{t('patient.journey.trackFromDashboard')}</span>
              </li>
            </ol>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={() => setShowEmailModal(true)}
              variant="outline"
              className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Mail className="h-4 w-4 mr-2" />
              {t('patient.notifSettings.channelEmail')}
            </Button>
            
            <Button
              onClick={handleDownloadReceipt}
              variant="outline"
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('patient.paymentSuccess.downloadReceipt')}
            </Button>
            
            <Button
              onClick={() => {
                // Reset flow for new bookings but keep session storage clean
                resetFlow();
                sessionStorage.removeItem('lastPayment');
                sessionStorage.removeItem('paidOffer');
                navigate('/patient/dashboard');
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Home className="h-4 w-4 mr-2" />
              {t('patient.paymentSuccess.backToDashboard')}
            </Button>
          </div>

          {/* Support Notice */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact our support team at <a href="mailto:support@treatx.health" className="text-blue-600 hover:underline">support@treatx.health</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;